import { useEffect, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import { Iconify } from '@mfw/components';
import Button from '@mui/material/Button';
import { useSnackbar } from 'notistack';
import { useIntl } from 'react-intl';

import { createAppVersionChecker } from './appVersionChecker';
import messages from './messages';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const SNACKBAR_KEY = 'app-version-update';

// DefinePlugin inlines APP_VERSION at build time; a bundle built without the
// define (e.g. a dev server started before the config change) must not crash
// — version checking is simply disabled then.
const currentVersion = typeof APP_VERSION === 'undefined' ? null : APP_VERSION;

async function fetchServerVersion() {
  const response = await fetch('/version.json', { cache: 'no-store' });
  if (!response.ok) return null;
  const data: unknown = await response.json();
  if (
    typeof data === 'object' &&
    data !== null &&
    'version' in data &&
    typeof data.version === 'string'
  ) {
    return data.version;
  }
  return null;
}

/**
 * Polls /version.json (emitted by the build with the bundle's APP_VERSION)
 * and shows a persistent "new version available" snackbar after a deploy, so
 * users refresh on their own terms instead of crashing on a stale chunk.
 * Renders nothing.
 */
export const AppVersionCheck = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();

  const notify = () => {
    enqueueSnackbar(formatMessage(messages.newVersionAvailable), {
      action: (
        <>
          <Button
            data-testid="app-version-refresh"
            onClick={() => window.location.reload()}
            variant="outlined"
            color="inherit"
            size="small"
          >
            {formatMessage(messages.refresh)}
          </Button>
          <IconButton
            onClick={() => closeSnackbar(SNACKBAR_KEY)}
            className="p-1"
            size="small"
          >
            <Iconify
              className="text-text-primary"
              icon="mingcute:close-line"
              size="small"
            />
          </IconButton>
        </>
      ),
      key: SNACKBAR_KEY,
      preventDuplicate: true,
      variant: 'info',
      persist: true,
    });
  };
  const notifyRef = useRef(notify);
  notifyRef.current = notify;

  const [checker] = useState(() =>
    currentVersion
      ? createAppVersionChecker({
          currentVersion,
          fetchVersion: fetchServerVersion,
          onNewVersion: () => notifyRef.current(),
        })
      : null,
  );

  useEffect(() => {
    if (!checker) return undefined;
    const check = () => {
      checker.check();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };
    const intervalId = window.setInterval(check, CHECK_INTERVAL_MS);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [checker]);

  return null;
};
